import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Trash2, ChevronLeft, ShoppingBag, ArrowRight, AlertCircle, Clock } from "lucide-react";
import { useI18n } from "@/i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const shopApi = axios.create({ baseURL: `${BACKEND}/api/public/shop` });
const img = (u) => (u ? `${BACKEND}${u}` : null);
const CART_KEY = (slug) => `mayr_cart_${slug}`;

export function useCartToken(slug) {
  const [token, setToken] = useState(() => localStorage.getItem(CART_KEY(slug)) || "");
  const ensure = useCallback(async () => {
    let tk = localStorage.getItem(CART_KEY(slug));
    if (tk) {
      try { await shopApi.get(`/cart/${tk}`); return tk; } catch { tk = ""; }
    }
    const { data } = await shopApi.post(`/cart?slug=${slug}`);
    localStorage.setItem(CART_KEY(slug), data.token);
    setToken(data.token);
    return data.token;
  }, [slug]);
  return { token, ensure, setToken };
}

export default function ShopCart() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [cart, setCart] = useState(null);
  const [busy, setBusy] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState("");
  const [consent, setConsent] = useState(false);
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState("");
  const [contact, setContact] = useState({ email: "", name: "", line1: "", line2: "", postal_code: "", city: "", country: "DE" });
  const [options, setOptions] = useState([]);

  const errMsg = (detail) => {
    const k = `shop.storefront.err_${detail || "generic"}`;
    const v = t(k);
    return v === k ? t("shop.storefront.err_generic") : v;
  };

  const load = useCallback(async () => {
    const tk = localStorage.getItem(CART_KEY(slug));
    if (!tk) { setCart({ items: [], totals: { total: "0.00" } }); setBusy(false); return; }
    try {
      const { data } = await shopApi.get(`/cart/${tk}`, { params: { lang: locale } });
      setCart(data);
      if (data.contact?.email) setContact((c) => ({ ...c, ...data.contact }));
      const o = await shopApi.get(`/cart/${tk}/shipping-options`, { params: { country: data.contact?.country || "DE" } });
      setOptions(o.data.options || []);
    } catch { localStorage.removeItem(CART_KEY(slug)); setCart({ items: [], totals: { total: "0.00" } }); }
    setBusy(false);
  }, [slug, locale]);

  useEffect(() => { load(); }, [load]);

  // enabled payment methods are decided server-side (tenant config)
  useEffect(() => {
    shopApi.get(`/${slug}/payment-methods`)
      .then(({ data }) => { setMethods(data.methods || []); setMethod(data.default || (data.methods || [])[0] || ""); })
      .catch(() => { setMethods(["manual_invoice"]); setMethod("manual_invoice"); });
  }, [slug]);

  const tk = () => localStorage.getItem(CART_KEY(slug));
  const guard = async (fn) => {
    setError("");
    try { const { data } = await fn(); setCart(data); return data; }
    catch (e) { setError(errMsg(e?.response?.data?.detail)); throw e; }
  };
  const setQty = (pid, qty) => guard(() => shopApi.post(`/cart/${tk()}/items`, { product_id: pid, qty }, { params: { lang: locale } })).catch(() => {});
  const remove = (pid) => guard(() => shopApi.delete(`/cart/${tk()}/items/${pid}`)).catch(() => {});
  const chooseShipping = (rid) => guard(() => shopApi.post(`/cart/${tk()}/shipping`, { rate_id: rid })).catch(() => {});
  const saveContact = async () => {
    await guard(() => shopApi.post(`/cart/${tk()}/contact`, contact)).catch(() => {});
    const o = await shopApi.get(`/cart/${tk()}/shipping-options`, { params: { country: contact.country } });
    setOptions(o.data.options || []);
  };
  const placeOrder = async () => {
    setError("");
    if (!consent) { setError(t("shop.storefront.consent_required")); return; }
    setPlacing(true);
    try {
      await guard(() => shopApi.post(`/cart/${tk()}/contact`, contact));
      const { data } = await shopApi.post(`/cart/${tk()}/checkout`, { provider: method });
      localStorage.removeItem(CART_KEY(slug));
      navigate(`/shop/${slug}/order/${data.pay_token}`);
    } catch (e) { setError(errMsg(e?.response?.data?.detail)); }
    finally { setPlacing(false); }
  };

  if (busy) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const items = cart?.items || [];
  return (
    <div className="min-h-screen bg-slate-50" data-testid="sf-cart">
      <div className="mx-auto max-w-4xl px-4 py-6">
        <Link to={`/shop/${slug}`} className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800"><ChevronLeft className="h-4 w-4" />{t("shop.storefront.back")}</Link>
        <h1 className="mb-6 text-2xl font-bold text-slate-900">{t("shop.storefront.cart")}</h1>

        {error && (
          <div role="alert" aria-live="assertive" data-testid="sf-cart-error" className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />{error}
          </div>
        )}

        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center text-slate-400" data-testid="sf-cart-empty">{t("shop.storefront.cart_empty")}</div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-3 lg:col-span-2">
              {items.map((it) => (
                <div key={it.product_id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3" data-testid={`sf-cart-item-${it.product_id}`}>
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded bg-slate-50">
                    {it.image_url ? <img src={img(it.image_url)} alt={it.name} className="h-full w-full object-cover" /> : <ShoppingBag className="h-6 w-6 text-slate-300" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-800">{it.name}</div>
                    <div className="text-xs text-slate-400">
                      {it.sku} · {it.discounted && it.list_price_net ? <span className="mr-1 line-through">€ {it.list_price_net}</span> : null}€ {it.unit_price_net} {t("shop.storefront.excl_vat")}
                    </div>
                    {it.availability === "out_of_stock" && <div className="text-xs font-medium text-red-500" data-testid={`sf-stock-${it.product_id}`}>{t("shop.storefront.out_of_stock")}</div>}
                    {it.availability === "low" && <div className="text-xs font-medium text-amber-600" data-testid={`sf-stock-${it.product_id}`}>{t("shop.storefront.stock_left").replace("{n}", it.available)}</div>}
                  </div>
                  <input type="number" min={it.min_qty} max={it.max_qty || undefined} value={it.qty}
                    aria-label={`${t("shop.products.min_qty")} ${it.name}`}
                    onChange={(e) => setQty(it.product_id, Math.max(1, parseInt(e.target.value || "1", 10)))}
                    className="w-16 rounded-md border border-slate-200 px-2 py-1 text-sm" data-testid={`sf-cart-qty-${it.product_id}`} />
                  <div className="w-20 text-right text-sm font-semibold" data-testid={`sf-cart-line-${it.product_id}`}>€ {it.line_total}</div>
                  <button onClick={() => remove(it.product_id)} aria-label={`${t("shop.categories.delete")} ${it.name}`} data-testid={`sf-cart-remove-${it.product_id}`}><Trash2 className="h-4 w-4 text-red-400" /></button>
                </div>
              ))}

              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <h2 className="mb-3 text-sm font-semibold text-slate-700">{t("shop.storefront.billing_shipping")}</h2>
                <div className="grid gap-2 sm:grid-cols-2">
                  <input placeholder={t("shop.storefront.email")} aria-label={t("shop.storefront.email")} type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="sf-co-email" />
                  <input placeholder={t("shop.storefront.name")} aria-label={t("shop.storefront.name")} value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="sf-co-name" />
                  <input placeholder={t("shop.storefront.address")} aria-label={t("shop.storefront.address")} value={contact.line1} onChange={(e) => setContact({ ...contact, line1: e.target.value })} className="rounded-md border border-slate-200 px-3 py-2 text-sm sm:col-span-2" data-testid="sf-co-line1" />
                  <input placeholder={t("shop.storefront.postal_code")} aria-label={t("shop.storefront.postal_code")} value={contact.postal_code} onChange={(e) => setContact({ ...contact, postal_code: e.target.value })} className="rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="sf-co-postal" />
                  <input placeholder={t("shop.storefront.city")} aria-label={t("shop.storefront.city")} value={contact.city} onChange={(e) => setContact({ ...contact, city: e.target.value })} className="rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="sf-co-city" />
                </div>
                <button onClick={saveContact} className="mt-2 text-xs font-medium text-blue-600" data-testid="sf-co-save">{t("shop.storefront.load_shipping")}</button>
                {options.length > 0 && (
                  <fieldset className="mt-3 space-y-1" data-testid="sf-shipping-options">
                    <legend className="mb-1 text-xs font-semibold text-slate-500">{t("shop.orders.shipping")}</legend>
                    {options.map((o) => (
                      <label key={o.rate_id} className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 p-2 text-sm">
                        <input type="radio" name="ship" checked={cart.shipping?.rate_id === o.rate_id} onChange={() => chooseShipping(o.rate_id)} data-testid={`sf-ship-${o.rate_id}`} />
                        <span className="flex-1">{o.name}</span><span>€ {o.price_net}</span>
                      </label>
                    ))}
                  </fieldset>
                )}
              </div>
            </div>

            <div className="h-fit rounded-xl border border-slate-200 bg-white p-4" data-testid="sf-cart-summary" aria-live="polite">
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">{t("shop.storefront.subtotal")}</span><span>€ {cart.totals.subtotal}</span></div>
                {cart.shipping && <div className="flex justify-between"><span className="text-slate-500">{cart.shipping.name}</span><span>€ {cart.shipping.price_net}</span></div>}
                <div className="flex justify-between"><span className="text-slate-500">{t("shop.storefront.vat")}</span><span>€ {cart.totals.tax_total}</span></div>
                <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 text-base font-bold"><span>{t("shop.storefront.total")}</span><span data-testid="sf-cart-total">€ {cart.totals.total}</span></div>
              </div>

              <fieldset className="mt-4">
                <legend className="mb-1 text-xs font-semibold text-slate-500">{t("shop.storefront.payment_method")}</legend>
                {methods.map((m) => (
                  <label key={m} className="flex cursor-pointer items-center gap-2 py-1 text-sm">
                    <input type="radio" name="pay" checked={method === m} onChange={() => setMethod(m)} data-testid={`sf-pay-${m}`} />
                    <span>{t(`shop.storefront.pay_${m}`)}</span>
                  </label>
                ))}
              </fieldset>

              <label className="mt-3 flex cursor-pointer items-start gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="sf-consent" className="mt-0.5" />
                <span>{t("shop.storefront.consent_label")}</span>
              </label>

              <p className="mt-3 flex items-center gap-1 text-[11px] text-slate-400"><Clock className="h-3 w-3" />{t("shop.storefront.reservation_note")}</p>

              <button onClick={placeOrder} disabled={placing || !cart.shipping || !consent || !method} data-testid="sf-place-order"
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50">
                {placing ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{t("shop.storefront.place_order")}<ArrowRight className="h-4 w-4" /></>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
